import { useCallback, useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { useMyUnits } from '~/data/orgunits';
import {
  RelationshipType,
  useGetManagerEmployeeRelationshipType,
} from '~/data/search';

type PropsType = {
  employeeId?: number;
  unitId?: number;
};

export type UseMyAccessToSelectedEmployeeOutput = {
  isLoading: boolean;
  isInMyUnits: boolean;
  managerRelationshipType?: RelationshipType;
  hasManagerRelationshipType: (
    expectedRelationships: RelationshipType[],
  ) => boolean;
};

export const useMyAccessToSelectedEmployee = (
  props: PropsType,
  swrConfig?: SWRConfiguration,
): UseMyAccessToSelectedEmployeeOutput => {
  const { employeeId, unitId } = props;

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const { myUnits } = useMyUnits();

  const { isInMyUnits } = useMemo(() => {
    return {
      isInMyUnits:
        !!unitId && myUnits.some((orgUnit) => orgUnit.fOrgUnitId === unitId),
    };
  }, [unitId, myUnits]);

  const { data: relationship, isLoading: isLoadingRelationship } =
    useGetManagerEmployeeRelationshipType(
      {
        employeeId: loggedInEmployeeId,
        targetId: employeeId,
      },
      swrConfig,
    );

  const hasManagerRelationshipType = useCallback(
    (expectedRelationships: RelationshipType[]) =>
      !!relationship && expectedRelationships.includes(relationship),
    [relationship],
  );

  return {
    isLoading: isLoadingRelationship,
    isInMyUnits,
    managerRelationshipType: relationship,
    hasManagerRelationshipType,
  };
};
